import { TrendingUp } from "lucide-react";
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";

interface Metric {
  label: string;
  value: number;
}

interface PerformanceCardProps {
  title?: string;
  metrics: Metric[];
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const { label, value } = payload[0].payload;
    return (
      <div
        style={{
          background: "#fff",
          padding: "10px 14px",
          borderRadius: "10px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          fontWeight: 500,
        }}
      >
        <p style={{ margin: 0, color: "#374151" }}>{label}</p>
        <p style={{ margin: 0, color: "#009ddf" }}>{value}%</p>
      </div>
    );
  }
  return null;
};

const PerformanceComparisonCard: React.FC<{ data: PerformanceCardProps }> = ({
  data,
}) => {
  const chartData = data.metrics.map((item, index) => ({
    label: item.label,
    value: item.value,
    gradientId: `gradient-${index}`,
  }));

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <span style={styles.icon}>
          <TrendingUp />
        </span>
        <h3 style={styles.title}>{data.title || "Performance Comparison"}</h3>
      </div>

      <div style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
          >
            <defs>
              {chartData.map((entry, index) => (
                <linearGradient
                  key={entry.gradientId}
                  id={entry.gradientId}
                  x1="0%"
                  y1="100%"
                  x2="0%"
                  y2="0%"
                >
                  {entry.label === "Creativity" && (
                    <>
                      <stop offset="0%" stopColor="#4ade80" />
                      <stop offset="100%" stopColor="#22c55e" />
                    </>
                  )}
                  {entry.label === "Structure" && (
                    <>
                      <stop offset="0%" stopColor="#facc15" />
                      <stop offset="100%" stopColor="#f97316" />
                    </>
                  )}
                  {entry.label === "Grammar" && (
                    <>
                      <stop offset="0%" stopColor="#f87171" />
                      <stop offset="100%" stopColor="#ef4444" />
                    </>
                  )}
                  {entry.label === "Overall" && (
                    <>
                      <stop offset="0%" stopColor="#5babed" />
                      <stop offset="100%" stopColor="#009ddf" />
                    </>
                  )}
                </linearGradient>
              ))}
            </defs>

            <CartesianGrid
              strokeDasharray="4 6"
              horizontal={true}
              vertical={false}
              stroke="#e5e7eb"
            />

            <YAxis
              ticks={[0, 20, 40, 60, 80, 100]}
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
            />

            <XAxis
              dataKey="label"
              tick={{ fontSize: 13, fill: "#374151" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={false}
            />

            {/* 🔹 Tooltip Enabled */}
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f3f4f6" }} />

            <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={40}>
              {chartData.map((entry) => (
                <Cell key={entry.label} fill={`url(#${entry.gradientId})`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 16,
    background: "#fff",
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 28,
  },
  icon: { fontSize: "24px", color: "#009ddf" },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
};

export default PerformanceComparisonCard;
